import React from 'react';
import { makeStyles } from '@mui/styles';
import { ReportProblemOutlined } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '@Common/models/redux/store';
import Carousel from 'nuka-carousel';

const data = [
  {
    title: 'Verbatim Plagiarism',
    text: "Your writing contains a copy of someone else's work in the exact way they wrote it.",
  },
  {
    title: 'Mosaic Plagiarism',
    text: "You added pieces of someone else's work into your own without citing or giving them credit.",
  },
  {
    title: 'Inadequate Paraphrase',
    text: 'Your paraphrase is too similar in wording to the original source, so much so that they could be mistaken for one another.',
  },
  {
    title: 'Uncited Paraphrase',
    text: 'Your paraphrase is well written but doesn’t contain a citation to credit the original author.',
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '60px 0 12px',
    maxWidth: 665,
    margin: '0 auto',
    [theme.breakpoints.down('sm')]: {
      padding: '60px 32px 0',
      overflow: 'hidden',
    },
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: 600,
    margin: '0 0 20px',
  },
  sectionText: {
    fontSize: 16,
  },
  cardsGrid: {
    marginTop: 40,
    display: 'grid',
    gap: 12,
    gridTemplateColumns: '1fr 1fr',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      margin: '40px -32px',
    },
  },
  mobileSlider: {
    // margin: '0 -16px',
    '& .slider-slide': {
      padding: '16px',
    },
    '& .slider-control-bottomcenter': {
      bottom: '-52px !important',
      '& .paging-item button svg': {
        display: 'none',
      },
      '& .paging-item': {
        marginLeft: 6,
      },
      '& .paging-item button': {
        width: '8px',
        height: '8px',
        borderRadius: '100px',
        opacity: '1 !important',
        backgroundColor: `${theme.specialColors.CIRCULAR_PROGRESS}  !important`,
        padding: 0,
      },
      '& .paging-item.active button': {
        backgroundColor: `${theme.palette.primary.main} !important`,
        width: '18px',
        borderRadius: '50px',
      },
    },
  },
  plagiarismCard: {
    backgroundColor: '#FFB0B3',
    borderRadius: 8,
    padding: '20px 25px',
    [theme.breakpoints.down('sm')]: {
      minHeight: 172,
    },
  },
  cardIcon: {
    fontSize: 32,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#36080A',
    margin: '8px 0',
    lineHeight: '22px',
  },
  cardText: {
    color: '#36080A',
    lineHeight: '19px',
  },
}));

function Plagiarism() {
  const isMobile = useSelector((state: RootState) => state.common.platform.isMobile);
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <h2 className={classes.sectionTitle}>Paraphrasing and Avoiding Plagiarism</h2>
      <div className={classes.sectionText}>
        <p>
          When you’re paraphrasing, unintentional plagiarism is the biggest pitfall you can
          encounter.{' '}
        </p>
        <p>
          This is because sometimes we remember the source material too well and don’t do our due
          diligence to recheck our paraphrases against the original. Accidental plagiarism of this
          kind can be avoided by double checking our work and also ensuring we cite all outside
          sources, whether they are paraphrased or not.
        </p>
        <p>
          According to the{' '}
          <a
            href="https://usingsources.fas.harvard.edu/what-constitutes-plagiarism"
            style={{ textDecoration: 'underline' }}
            target="_blank"
            rel="noreferrer"
          >
            Harvard College Writing Program
          </a>
          , there are six main types of plagiarism, four of which apply to paraphrasing. If your
          paraphrases don’t fit under any of these categories, you’ll be free and clear of this
          issue.
        </p>
      </div>
      {isMobile ? (
        <div className={classes.cardsGrid}>
          <Carousel
            className={classes.mobileSlider}
            slideWidth={1}
            cellSpacing={16}
            renderCenterLeftControls={null}
            renderCenterRightControls={null}
            framePadding="0"
            frameOverflow="visible"
          >
            {data.map((item) => (
              <PlagiarismCard key={item.title} title={item.title} text={item.text} />
            ))}
          </Carousel>
        </div>
      ) : (
        <div className={classes.cardsGrid}>
          {data.map((item) => (
            <PlagiarismCard key={item.title} title={item.title} text={item.text} />
          ))}
        </div>
      )}
    </div>
  );
}

export default Plagiarism;

function PlagiarismCard({ title, text }: any) {
  const classes = useStyles();

  return (
    <div className={classes.plagiarismCard}>
      <ReportProblemOutlined color="error" className={classes.cardIcon} />
      <div className={classes.cardTitle}>{title}</div>
      <div className={classes.cardText}>{text}</div>
    </div>
  );
}
